import { supabase } from '../lib/supabase';
import { routeMessageToAgent } from './openaiService';
import { searchSimilarChunks, createDocumentCitation } from './embeddingsService';

export const chatService = {
  // Get all agents
  async getAgents() {
    const { data, error } = await supabase?.from('agents')?.select('*')?.eq('is_active', true)?.order('name');

    if (error) throw error;

    return data?.map(agent => ({
      id: agent?.id,
      name: agent?.name,
      role: agent?.role,
      status: agent?.status,
      description: agent?.description,
      avatarUrl: agent?.avatar_url,
      specialization: agent?.specialization,
      isActive: agent?.is_active,
      createdAt: agent?.created_at,
      updatedAt: agent?.updated_at
    })) || [];
  },

  // Get all projects
  async getProjects() {
    const { data, error } = await supabase?.from('chat_projects')?.select('*')?.order('name');

    if (error) throw error;

    return data?.map(project => ({
      id: project?.id,
      name: project?.name,
      description: project?.description,
      createdAt: project?.created_at
    })) || [];
  },

  // Get user conversations
  async getConversations(userId) {
    const { data, error } = await supabase?.from('chat_conversations')?.select(`
        *,
        agents (*),
        chat_projects (*)
      `)?.eq('user_id', userId)?.order('last_message_at', { ascending: false });

    if (error) throw error;

    return data?.map(conv => ({
      id: conv?.id,
      userId: conv?.user_id,
      agentId: conv?.agent_id,
      projectId: conv?.project_id,
      title: conv?.title,
      lastMessageAt: conv?.last_message_at,
      createdAt: conv?.created_at,
      agent: conv?.agents ? {
        id: conv?.agents?.id,
        name: conv?.agents?.name,
        role: conv?.agents?.role,
        status: conv?.agents?.status,
        avatarUrl: conv?.agents?.avatar_url
      } : null,
      project: conv?.chat_projects ? {
        id: conv?.chat_projects?.id,
        name: conv?.chat_projects?.name
      } : null
    })) || [];
  },

  // Create new conversation
  async createConversation(userId, agentId, projectId = null, title = null) {
    const { data, error } = await supabase?.from('chat_conversations')?.insert({
        user_id: userId,
        agent_id: agentId,
        project_id: projectId,
        title: title
      })?.select(`
        *,
        agents (*),
        chat_projects (*)
      `)?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      agentId: data?.agent_id,
      projectId: data?.project_id,
      title: data?.title,
      lastMessageAt: data?.last_message_at,
      createdAt: data?.created_at,
      agent: data?.agents ? {
        id: data?.agents?.id,
        name: data?.agents?.name,
        role: data?.agents?.role,
        status: data?.agents?.status,
        avatarUrl: data?.agents?.avatar_url
      } : null,
      project: data?.chat_projects ? {
        id: data?.chat_projects?.id,
        name: data?.chat_projects?.name
      } : null
    };
  },

  // Get messages for conversation
  async getMessages(conversationId) {
    const { data, error } = await supabase?.from('chat_messages')?.select(`
        *,
        message_tags (*)
      `)?.eq('conversation_id', conversationId)?.order('created_at', { ascending: true });

    if (error) throw error;

    return data?.map(msg => ({
      id: msg?.id,
      conversationId: msg?.conversation_id,
      senderType: msg?.sender_type,
      senderId: msg?.sender_id,
      content: msg?.content,
      status: msg?.status,
      isStreaming: msg?.is_streaming,
      metadata: msg?.metadata,
      createdAt: msg?.created_at,
      tags: msg?.message_tags?.map(tag => tag?.tag) || []
    })) || [];
  },

  // Send message
  async sendMessage(conversationId, userId, content, tags = []) {
    const { data: message, error: messageError } = await supabase?.from('chat_messages')?.insert({
        conversation_id: conversationId,
        sender_type: 'user',
        sender_id: userId,
        content: content,
        status: 'sent'
      })?.select()?.single();

    if (messageError) throw messageError;

    // Add tags if provided
    if (tags?.length > 0) {
      const tagInserts = tags?.map(tag => ({
        message_id: message?.id,
        tag: tag
      }));

      const { error: tagError } = await supabase?.from('message_tags')?.insert(tagInserts);

      if (tagError) console.error('Tag insertion error:', tagError);
    }

    return {
      id: message?.id,
      conversationId: message?.conversation_id,
      senderType: message?.sender_type,
      senderId: message?.sender_id,
      content: message?.content,
      status: message?.status,
      isStreaming: message?.is_streaming,
      metadata: message?.metadata,
      createdAt: message?.created_at,
      tags: tags
    };
  },

  // Simulate agent streaming response
  async sendAgentMessage(conversationId, agentId, content, isStreaming = false) {
    const { data, error } = await supabase?.from('chat_messages')?.insert({
        conversation_id: conversationId,
        sender_type: 'agent',
        sender_id: agentId,
        content: content,
        is_streaming: isStreaming,
        status: 'sent'
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      conversationId: data?.conversation_id,
      senderType: data?.sender_type,
      senderId: data?.sender_id,
      content: data?.content,
      status: data?.status,
      isStreaming: data?.is_streaming,
      metadata: data?.metadata,
      createdAt: data?.created_at
    };
  },

  // Update message content (for streaming)
  async updateMessageContent(messageId, content, isStreaming = false) {
    const { data, error } = await supabase?.from('chat_messages')?.update({
        content: content,
        is_streaming: isStreaming
      })?.eq('id', messageId)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      conversationId: data?.conversation_id,
      senderType: data?.sender_type,
      senderId: data?.sender_id,
      content: data?.content,
      status: data?.status,
      isStreaming: data?.is_streaming,
      metadata: data?.metadata,
      createdAt: data?.created_at
    };
  },

  // Generate AI-powered agent response with streaming
  async generateAndSendAgentResponse(conversationId, agent, conversationHistory, userMessage) {
    try {
      // Get conversation details to access project_id
      let documentContext = '';
      let relevantChunks = [];
      
      try {
        const { data: conversation, error: convError } = await supabase?.from('chat_conversations')?.select('project_id')?.eq('id', conversationId)?.single();
        
        if (!convError && conversation?.project_id) {
          const projectId = conversation?.project_id;
          
          // Search for relevant document chunks with error boundary
          try {
            relevantChunks = await searchSimilarChunks(userMessage, {
              projectId,
              matchThreshold: 0.75,
              matchCount: 3
            });

            if (relevantChunks?.length > 0) {
              documentContext = '\n\n--- RELEVANT PROJECT DOCUMENTS ---\n';
              relevantChunks?.forEach((chunk, index) => {
                documentContext += `\n[Document ${index + 1}: ${chunk?.documentName} (${chunk?.documentType})]\n`;
                documentContext += `${chunk?.content}\n`;
                documentContext += `(Relevance: ${(chunk?.similarity * 100)?.toFixed(1)}%)\n`;
              });
              documentContext += '\n--- END DOCUMENTS ---\n\n';
              documentContext += 'Please cite these documents when relevant to your response.\n';
            }
          } catch (embeddingError) {
            console.error('Error retrieving document context (non-fatal):', embeddingError);
            // Continue without document context - chat must not fail
            documentContext = '';
            relevantChunks = [];
          }
        }
      } catch (projectError) {
        console.error('Error fetching project context (non-fatal):', projectError);
        // Continue without project context - chat must not fail
      }

      // Enhance conversation history with document context if available
      const enhancedHistory = documentContext 
        ? [...conversationHistory, { senderType: 'system', content: documentContext }]
        : conversationHistory;

      // Use multi-agent routing system
      const routingResult = await routeMessageToAgent(userMessage, enhancedHistory);

      // If clarification needed, send coordinator message
      if (routingResult?.needsClarification) {
        const clarificationMessage = await this.sendAgentMessage(
          conversationId,
          agent?.id,
          routingResult?.message,
          false
        );
        return {
          message: routingResult?.message,
          agent: routingResult?.agent,
          routing: routingResult?.routing
        };
      }

      // Create initial streaming message
      const initialMessage = await this.sendAgentMessage(
        conversationId,
        agent?.id,
        '',
        true
      );

      let fullResponse = '';

      // Process streaming response from specialist agent
      for await (const chunk of routingResult?.stream) {
        const content = chunk?.choices?.[0]?.delta?.content || '';
        if (content) {
          fullResponse += content;
          // Update message with accumulated content
          await this.updateMessageContent(initialMessage?.id, fullResponse, true);
        }
      }

      // Final update to mark streaming complete
      await this.updateMessageContent(initialMessage?.id, fullResponse, false);

      // Create citations for relevant documents with error boundary
      if (relevantChunks?.length > 0) {
        try {
          for (const chunk of relevantChunks) {
            await createDocumentCitation(
              initialMessage?.id,
              chunk?.documentId,
              chunk?.chunkId,
              chunk?.similarity
            );
          }
        } catch (citationError) {
          console.error('Error creating citations (non-fatal):', citationError);
          // Continue without citations - chat must not fail
        }
      }

      return {
        message: fullResponse,
        agent: routingResult?.agent,
        routing: routingResult?.routing,
        citations: relevantChunks?.length || 0
      };
    } catch (error) {
      console.error('Error generating agent response:', error?.message);
      throw error;
    }
  },

  // Subscribe to new messages in conversation
  subscribeToMessages(conversationId, callback) {
    const channel = supabase?.channel(`messages:${conversationId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          callback({
            id: payload?.new?.id,
            conversationId: payload?.new?.conversation_id,
            senderType: payload?.new?.sender_type,
            senderId: payload?.new?.sender_id,
            content: payload?.new?.content,
            status: payload?.new?.status,
            isStreaming: payload?.new?.is_streaming,
            metadata: payload?.new?.metadata,
            createdAt: payload?.new?.created_at
          });
        }
      )?.subscribe();

    return channel;
  },

  // Subscribe to message updates (for streaming)
  subscribeToMessageUpdates(conversationId, callback) {
    const channel = supabase?.channel(`message-updates:${conversationId}`)?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          callback({
            id: payload?.new?.id,
            conversationId: payload?.new?.conversation_id,
            senderType: payload?.new?.sender_type,
            senderId: payload?.new?.sender_id,
            content: payload?.new?.content,
            status: payload?.new?.status,
            isStreaming: payload?.new?.is_streaming,
            metadata: payload?.new?.metadata,
            createdAt: payload?.new?.created_at
          });
        }
      )?.subscribe();

    return channel;
  },

  // Subscribe to agent status changes
  subscribeToAgentStatus(callback) {
    const channel = supabase?.channel('agent-status')?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'agents'
        },
        (payload) => {
          callback({
            id: payload?.new?.id,
            name: payload?.new?.name,
            role: payload?.new?.role,
            status: payload?.new?.status,
            updatedAt: payload?.new?.updated_at
          });
        }
      )?.subscribe();

    return channel;
  },

  // Subscribe to conversation updates
  subscribeToConversations(userId, callback) {
    const channel = supabase?.channel(`conversations:${userId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_conversations',
          filter: `user_id=eq.${userId}`
        },
        async (payload) => {
          // Fetch full conversation data with relations
          const { data, error } = await supabase?.from('chat_conversations')?.select(`
              *,
              agents (*),
              chat_projects (*)
            `)?.eq('id', payload?.new?.id)?.single();

          if (!error && data) {
            callback({
              event: payload?.eventType,
              conversation: {
                id: data?.id,
                userId: data?.user_id,
                agentId: data?.agent_id,
                projectId: data?.project_id,
                title: data?.title,
                lastMessageAt: data?.last_message_at,
                createdAt: data?.created_at,
                agent: data?.agents ? {
                  id: data?.agents?.id,
                  name: data?.agents?.name,
                  role: data?.agents?.role,
                  status: data?.agents?.status,
                  avatarUrl: data?.agents?.avatar_url
                } : null,
                project: data?.chat_projects ? {
                  id: data?.chat_projects?.id,
                  name: data?.chat_projects?.name
                } : null
              }
            });
          }
        }
      )?.subscribe();

    return channel;
  },

  // Subscribe to team notifications
  subscribeToNotifications(userId, callback) {
    const channel = supabase?.channel(`notifications:${userId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'team_notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          callback({
            id: payload?.new?.id,
            userId: payload?.new?.user_id,
            notificationType: payload?.new?.notification_type,
            priority: payload?.new?.priority,
            title: payload?.new?.title,
            message: payload?.new?.message,
            relatedConversationId: payload?.new?.related_conversation_id,
            relatedMessageId: payload?.new?.related_message_id,
            relatedProjectId: payload?.new?.related_project_id,
            metadata: payload?.new?.metadata,
            isRead: payload?.new?.is_read,
            readAt: payload?.new?.read_at,
            createdAt: payload?.new?.created_at
          });
        }
      )?.subscribe();

    return channel;
  },

  // Subscribe to typing indicators
  subscribeToTypingIndicators(conversationId, callback) {
    const channel = supabase?.channel(`typing:${conversationId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'typing_indicators',
          filter: `conversation_id=eq.${conversationId}`
        },
        async (payload) => {
          if (payload?.eventType === 'DELETE') {
            callback({
              event: 'stopped',
              userId: payload?.old?.user_id,
              conversationId: payload?.old?.conversation_id
            });
          } else {
            // Fetch user details
            const { data: user } = await supabase?.from('user_profiles')?.select('full_name')?.eq('id', payload?.new?.user_id)?.single();
            
            callback({
              event: payload?.new?.is_typing ? 'started' : 'stopped',
              userId: payload?.new?.user_id,
              userName: user?.full_name || 'User',
              conversationId: payload?.new?.conversation_id,
              startedAt: payload?.new?.started_at,
              expiresAt: payload?.new?.expires_at
            });
          }
        }
      )?.subscribe();

    return channel;
  },

  // Subscribe to user presence
  subscribeToUserPresence(callback) {
    const channel = supabase?.channel('user-presence')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_presence'
        },
        (payload) => {
          callback({
            userId: payload?.new?.user_id,
            status: payload?.new?.status,
            lastSeen: payload?.new?.last_seen,
            currentConversationId: payload?.new?.current_conversation_id,
            updatedAt: payload?.new?.updated_at
          });
        }
      )?.subscribe();

    return channel;
  },

  // Get user notifications
  async getNotifications(userId, limit = 50) {
    const { data, error } = await supabase?.from('team_notifications')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(limit);

    if (error) throw error;

    return data?.map(notif => ({
      id: notif?.id,
      userId: notif?.user_id,
      notificationType: notif?.notification_type,
      priority: notif?.priority,
      title: notif?.title,
      message: notif?.message,
      relatedConversationId: notif?.related_conversation_id,
      relatedMessageId: notif?.related_message_id,
      relatedProjectId: notif?.related_project_id,
      metadata: notif?.metadata,
      isRead: notif?.is_read,
      readAt: notif?.read_at,
      createdAt: notif?.created_at
    })) || [];
  },

  // Mark notification as read
  async markNotificationRead(notificationId) {
    const { data, error } = await supabase?.from('team_notifications')?.update({
        is_read: true,
        read_at: new Date()?.toISOString()
      })?.eq('id', notificationId)?.select()?.single();

    if (error) throw error;

    return data;
  },

  // Mark all notifications as read
  async markAllNotificationsRead(userId) {
    const { error } = await supabase?.from('team_notifications')?.update({
        is_read: true,
        read_at: new Date()?.toISOString()
      })?.eq('user_id', userId)?.eq('is_read', false);

    if (error) throw error;

    return true;
  },

  // Update user presence
  async updateUserPresence(userId, status, conversationId = null) {
    const { data, error } = await supabase?.from('user_presence')?.upsert({
        user_id: userId,
        status: status,
        last_seen: new Date()?.toISOString(),
        current_conversation_id: conversationId
      }, {
        onConflict: 'user_id'
      })?.select()?.single();

    if (error) throw error;

    return data;
  },

  // Set typing indicator
  async setTypingIndicator(conversationId, userId, isTyping = true) {
    if (isTyping) {
      const expiresAt = new Date();
      expiresAt?.setSeconds(expiresAt?.getSeconds() + 10);

      const { data, error } = await supabase?.from('typing_indicators')?.upsert({
          conversation_id: conversationId,
          user_id: userId,
          is_typing: true,
          started_at: new Date()?.toISOString(),
          expires_at: expiresAt?.toISOString()
        }, {
          onConflict: 'conversation_id,user_id'
        })?.select()?.single();

      if (error) throw error;
      return data;
    } else {
      const { error } = await supabase?.from('typing_indicators')?.delete()?.eq('conversation_id', conversationId)?.eq('user_id', userId);

      if (error) throw error;
      return null;
    }
  },

  // Get conversation participants
  async getConversationParticipants(conversationId) {
    const { data, error } = await supabase?.from('conversation_participants')?.select(`
        *,
        user_profiles (*)
      `)?.eq('conversation_id', conversationId)?.order('joined_at');

    if (error) throw error;

    return data?.map(participant => ({
      id: participant?.id,
      conversationId: participant?.conversation_id,
      userId: participant?.user_id,
      role: participant?.role,
      joinedAt: participant?.joined_at,
      lastReadAt: participant?.last_read_at,
      user: participant?.user_profiles ? {
        id: participant?.user_profiles?.id,
        email: participant?.user_profiles?.email,
        fullName: participant?.user_profiles?.full_name,
        avatarUrl: participant?.user_profiles?.avatar_url
      } : null
    })) || [];
  },

  // Add conversation participant
  async addConversationParticipant(conversationId, userId, role = 'member') {
    const { data, error } = await supabase?.from('conversation_participants')?.insert({
        conversation_id: conversationId,
        user_id: userId,
        role: role
      })?.select()?.single();

    if (error) throw error;

    return data;
  },

  // Unsubscribe from channel
  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};